/**
 * Place search — spec v0.2 §5, backed by the Worker's Photon proxy.
 *
 * Typing is the primary way people give a destination; tapping the map is the
 * convenience. Both write to the same endpoint state.
 */

export interface Place {
  label: string
  detail: string
  lat: number
  lng: number
}

export interface SearchBox {
  /** Show a label without triggering a search — e.g. after a map tap. */
  setLabel(text: string): void
  input: HTMLInputElement
}

import { API_BASE } from './api-base'
import { track } from './session'

async function geocode(q: string, signal: AbortSignal): Promise<Place[]> {
  const res = await fetch(`${API_BASE}/api/geocode?q=${encodeURIComponent(q)}`, { signal })
  if (!res.ok) return []
  const body = (await res.json()) as { results?: Place[] }
  return (body.results ?? []).filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lng))
}

export function createSearchBox(
  input: HTMLInputElement,
  list: HTMLUListElement,
  onPick: (place: Place) => void,
): SearchBox {
  let controller: AbortController | null = null
  let debounce: number | undefined
  let suppress = false

  function close() {
    list.hidden = true
    list.innerHTML = ''
    list.style.removeProperty('max-height')
  }

  /**
   * Place the dropdown as a viewport-fixed overlay above the input.
   *
   * It used to be `position: absolute` inside the bottom sheet, and the sheet
   * has `overflow-y: auto` — so the list was clipped to the sheet's box. On a
   * phone with the keyboard up that left one and a half suggestions visible,
   * with the first one sliced in half.
   *
   * Fixed positioning escapes the clip. The height is then capped so a strip
   * of map always stays visible: you should never lose your sense of where you
   * are just because you started typing.
   */
  function place() {
    const field = input.getBoundingClientRect()
    const MAP_STRIP = Math.max(96, Math.round(window.innerHeight * 0.18))
    const GAP = 6

    const available = field.top - MAP_STRIP - GAP
    list.style.position = 'fixed'
    list.style.left = `${Math.round(field.left)}px`
    list.style.width = `${Math.round(field.width)}px`
    list.style.bottom = `${Math.round(window.innerHeight - field.top + GAP)}px`
    // Never taller than the space above the field, never shorter than one row.
    list.style.maxHeight = `${Math.max(64, Math.round(available))}px`
  }

  function render(places: Place[]) {
    if (places.length === 0) return close()
    place()
    list.innerHTML = places
      .map(
        (p, i) =>
          `<li role="option" data-i="${i}"><b>${escape(p.label)}</b>` +
          (p.detail ? `<span>${escape(p.detail)}</span>` : '') +
          `</li>`,
      )
      .join('')
    list.hidden = false

    list.querySelectorAll('li').forEach((li) => {
      li.addEventListener('mousedown', (ev) => {
        // mousedown, not click: blur would close the list first.
        ev.preventDefault()
        const place = places[Number((li as HTMLElement).dataset.i)]
        suppress = true
        input.value = place.label
        close()
        onPick(place)
      })
    })
  }

  input.addEventListener('input', () => {
    if (suppress) {
      suppress = false
      return
    }
    window.clearTimeout(debounce)
    const q = input.value.trim()
    if (q.length < 2) return close()

    // Debounced so a typed word is one request, not eight — komoot's public
    // Photon instance is fair-use.
    debounce = window.setTimeout(async () => {
      controller?.abort()
      controller = new AbortController()
      try {
        track('search', {}, true)
        render(await geocode(q, controller.signal))
      } catch {
        close()
      }
    }, 250)
  })

  input.addEventListener('blur', () => window.setTimeout(close, 120))
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      close()
      input.blur()
    }
  })

  // Returning to a field should let you retype immediately rather than making
  // you clear the old value first.
  input.addEventListener('focus', () => {
    if (input.value) {
      // Deferred: iOS Safari collapses the selection if you call select()
      // synchronously inside focus.
      window.setTimeout(() => input.select(), 0)
    }
  })

  // The keyboard opening, or an orientation change, moves the field.
  const reposition = () => {
    if (!list.hidden) place()
  }
  window.addEventListener('resize', reposition)
  window.visualViewport?.addEventListener('resize', reposition)
  window.visualViewport?.addEventListener('scroll', reposition)

  return {
    setLabel(text: string) {
      suppress = true
      input.value = text
      close()
    },
    input,
  }
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => `&${{ '&': 'amp', '<': 'lt', '>': 'gt', '"': 'quot' }[c]};`)
}
